<template>
  <div class="ren-decoration-template-detail-container">
    <header class="ren-decoration-template-detail__head">
      <RrButton size="small" @click="$emit('back')">返回模板市场</RrButton>
      <div class="ren-decoration-template-detail__head-info">
        <p class="ren-decoration-template-detail__head-eyebrow">Template Detail</p>
        <h3 class="ren-decoration-template-detail__head-title">{{ template.templateName }}</h3>
        <span class="ren-decoration-template-detail__head-desc">{{ template.description || template.templateCode }}</span>
      </div>
      <div class="ren-decoration-template-detail__apply">
        <label class="ren-decoration-template-detail__target-label">
          <span>应用到</span>
          <RrSelect
            :value="targetPageCode"
            :options="targetPageOptions"
            :disabled="applyingTemplate"
            style="width: 140px"
            @update:value="$emit('update:targetPageCode', String($event))"
          />
        </label>
        <RrButton
          v-if="templateActionsEnabled"
          type="primary"
          :disabled="readonly || applyingTemplate || !templateEnabled"
          :loading="applyingTemplate"
          @click="$emit('apply', template, targetPageCode)"
        >
          {{ applyingTemplate ? '正在应用...' : '使用并进入装修' }}
        </RrButton>
      </div>
    </header>

    <div class="ren-decoration-template-detail">
      <section class="ren-decoration-template-detail__preview">
        <div
          class="ren-decoration-template-phone"
          :style="{ background: templateBackgroundColor }"
        >
          <img
            v-if="template.coverImageUrl"
            :alt="template.templateName"
            :src="template.coverImageUrl"
          />
          <template v-else>
            <div class="ren-decoration-template-phone__bar">
              <span></span>
              <strong>{{ previewTitle }}</strong>
              <span></span>
            </div>
            <div
              class="ren-decoration-template-phone__hero"
              :style="{ background: themeColor }"
            >
              <strong>{{ previewTitle }}</strong>
              <span>{{ template.description || template.templateCode }}</span>
            </div>
            <div class="ren-decoration-template-phone__blocks">
              <span v-for="component in previewComponents" :key="component.id">
                {{ componentPreviewLabel(component) }}
              </span>
            </div>
          </template>
        </div>
      </section>

      <aside class="ren-decoration-template-detail__info">
        <div class="ren-decoration-market-card__tags">
          <RrTag size="small">{{ scopeLabel }}</RrTag>
          <RrTag size="small">{{ typeLabel }}</RrTag>
          <RrTag size="small">{{ pageTypeLabelText }}</RrTag>
          <RrTag size="small" :type="templateEnabled ? 'success' : 'info'">{{ statusLabel }}</RrTag>
        </div>
        <dl class="ren-decoration-template-detail__meta-list">
          <div>
            <dt>模板编码</dt>
            <dd>{{ template.templateCode }}</dd>
          </div>
          <div>
            <dt>所属模块</dt>
            <dd>{{ template.moduleCode }}</dd>
          </div>
          <div>
            <dt>组件数量</dt>
            <dd>{{ componentCount }}</dd>
          </div>
          <div>
            <dt>更新时间</dt>
            <dd>{{ formatDate(template.updatedAt) }}</dd>
          </div>
          <div>
            <dt>目标页面</dt>
            <dd>{{ targetPageCode }}</dd>
          </div>
        </dl>

        <section class="ren-decoration-template-detail__components-section">
          <h4>组件清单</h4>
          <ul class="ren-decoration-template-components">
            <li v-for="component in allComponents" :key="component.id">
              <strong>{{ componentPreviewLabel(component) }}</strong>
              <span>{{ component.type }}</span>
            </li>
            <li v-if="allComponents.length === 0" class="is-empty">暂无组件</li>
          </ul>
        </section>

        <details class="ren-decoration-json">
          <summary>模板 JSON</summary>
          <pre><code>{{ formattedJson }}</code></pre>
        </details>
      </aside>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RrButton, RrSelect, RrTag } from '@xjlv2/ui'
import type { RenDecorationComponent, RenDecorationMarketTemplate } from '../../types'
import {
  formatTemplateDate,
  pageTypeLabel,
  parseTemplateSchema,
  templateEnabled as isTemplateEnabled,
  templateScopeLabel,
  templateStatusLabel,
  templateTypeLabel,
} from '../utils/template-market'

const props = withDefaults(
  defineProps<{
    template: RenDecorationMarketTemplate
    targetPageOptions?: Array<{ label: string; value: string }>
    targetPageCode?: string
    readonly?: boolean
    applyingTemplate?: boolean
    templateActionsEnabled?: boolean
  }>(),
  {
    targetPageOptions: () => [],
    targetPageCode: 'home',
    readonly: false,
    applyingTemplate: false,
    templateActionsEnabled: true,
  },
)

defineEmits<{
  back: []
  apply: [template: RenDecorationMarketTemplate, targetPageCode: string]
  'update:targetPageCode': [pageCode: string]
}>()

const parsedSchema = computed(() => parseTemplateSchema(props.template.schemaJson))
const templateEnabled = computed(() => isTemplateEnabled(props.template))
const templateBackgroundColor = computed(
  () => parsedSchema.value?.page?.backgroundColor || 'var(--rr-color-surface-subtle)',
)
const themeColor = computed(() => parsedSchema.value?.page?.themeColor || 'var(--rr-color-primary)')
const previewTitle = computed(() => parsedSchema.value?.page?.title || props.template.templateName)
const allComponents = computed<RenDecorationComponent[]>(() => parsedSchema.value?.components || [])
const previewComponents = computed(() => allComponents.value.slice(0, 8))
const componentCount = computed(() => allComponents.value.length)
const formattedJson = computed(() =>
  parsedSchema.value ? JSON.stringify(parsedSchema.value, null, 2) : props.template.schemaJson || '{}',
)
const scopeLabel = computed(() => templateScopeLabel(props.template))
const typeLabel = computed(() => templateTypeLabel(props.template.templateType))
const pageTypeLabelText = computed(() => pageTypeLabel(props.template.pageType))
const statusLabel = computed(() => templateStatusLabel(props.template))

function componentPreviewLabel(component: RenDecorationComponent) {
  return String(component.props?.title || component.title || component.type || '未命名组件')
}

function formatDate(dateStr?: string) {
  return formatTemplateDate(dateStr)
}
</script>

<style scoped>
.ren-decoration-template-detail-container {
  display: grid;
  gap: 16px;
}

.ren-decoration-template-detail__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  background: var(--rr-color-surface);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-md, 8px);
}

.ren-decoration-template-detail__head-info p,
.ren-decoration-template-detail__head-info h3 {
  margin: 0;
}

.ren-decoration-template-detail__head-eyebrow {
  font-size: var(--rr-font-size-xs, 12px);
  color: var(--rr-color-primary);
  font-weight: 700;
  text-transform: uppercase;
}

.ren-decoration-template-detail__head-title {
  font-size: var(--rr-font-size-lg, 18px);
  color: var(--rr-color-text-strong);
  margin-top: 2px;
}

.ren-decoration-template-detail__head-desc {
  font-size: var(--rr-font-size-xs, 12px);
  color: var(--rr-color-text-muted);
}

.ren-decoration-template-detail__apply {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ren-decoration-template-detail__target-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--rr-font-size-xs, 12px);
  color: var(--rr-color-text-muted);
}

.ren-decoration-template-detail {
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 20px;
}

.ren-decoration-template-detail__preview {
  display: flex;
  justify-content: center;
  padding: 20px;
  background: var(--rr-color-canvas);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-md, 8px);
}

.ren-decoration-template-phone {
  width: 320px;
  min-height: 520px;
  border: 8px solid #222;
  border-radius: 36px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.ren-decoration-template-phone__bar {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.04);
  font-size: 13px;
  font-weight: 600;
}

.ren-decoration-template-phone__hero {
  padding: 24px 16px;
  color: #fff;
  display: grid;
  gap: 6px;
}

.ren-decoration-template-phone__blocks {
  padding: 12px;
  display: grid;
  gap: 8px;
}

.ren-decoration-template-phone__blocks span {
  padding: 10px;
  background: var(--rr-color-surface);
  border: 1px solid var(--rr-color-border-subtle, var(--rr-color-border));
  border-radius: 6px;
  font-size: 12px;
  text-align: center;
  color: var(--rr-color-text);
}

.ren-decoration-template-detail__info {
  display: grid;
  gap: 16px;
  padding: 16px;
  background: var(--rr-color-surface);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-md, 8px);
}

.ren-decoration-market-card__tags {
  display: flex;
  gap: 6px;
}

.ren-tag {
  padding: 3px 8px;
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary);
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.ren-decoration-template-detail__meta-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  margin: 0;
}

.ren-decoration-template-detail__meta-list div {
  padding: 8px 12px;
  background: var(--rr-color-canvas);
  border-radius: 6px;
}

.ren-decoration-template-detail__meta-list dt {
  font-size: 11px;
  color: var(--rr-color-text-muted);
}

.ren-decoration-template-detail__meta-list dd {
  margin: 2px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--rr-color-text);
}

.ren-decoration-template-detail__components-section h4 {
  margin: 0 0 8px;
  font-size: 14px;
  color: var(--rr-color-text-strong);
}

.ren-decoration-template-components {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 6px;
  max-height: 200px;
  overflow: auto;
}

.ren-decoration-template-components li {
  display: flex;
  justify-content: space-between;
  padding: 6px 10px;
  background: var(--rr-color-canvas);
  border-radius: 4px;
  font-size: 12px;
}

.ren-decoration-json summary {
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: var(--rr-color-text-muted);
}

.ren-decoration-json pre {
  margin-top: 8px;
  padding: 10px;
  background: var(--rr-color-canvas);
  border-radius: 6px;
  font-family: var(--rr-font-mono, monospace);
  font-size: 11px;
  max-height: 180px;
  overflow: auto;
}
</style>
